import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Divider,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { FaCoins } from 'react-icons/fa';
// eslint-disable-next-line import/no-extraneous-dependencies
import { FaMoneyBills } from 'react-icons/fa6';
import type { IconBaseProps } from 'react-icons';

export interface Denomination {
  label: string;
  value: number;
}

interface DenominationInputsProps {
  bills: Denomination[];
  coins: Denomination[];
  onTotalChange?: (total: number) => void;
  onBillTotalChange?: (billTotal: number) => void;
  onCoinTotalChange?: (coinTotal: number) => void;
  values?: Record<string, string>;
  onValuesChange?: (next: Record<string, string>) => void;
  billsLabel?: string;
  coinsLabel?: string;
}

export const DenominationInputs: React.FC<DenominationInputsProps> = ({
  bills,
  coins,
  onTotalChange,
  onBillTotalChange,
  onCoinTotalChange,
  values,
  onValuesChange,
  billsLabel = 'Bills',
  coinsLabel = 'Coins',
}) => {
  const BillsIcon = FaMoneyBills as React.FC<IconBaseProps>;
  const CoinsIcon = FaCoins as React.FC<IconBaseProps>;

  const billRefs = useRef<Array<HTMLInputElement | null>>([]);
  const coinRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [uncontrolled, setUncontrolled] = useState<Record<string, string>>({});
  const isControlled = values !== undefined && onValuesChange !== undefined;
  const inputs = isControlled ? (values as Record<string, string>) : uncontrolled;

  const theme = useTheme();
  const primary = theme.palette.primary.main;

  const setInputs = (next: Record<string, string>) => {
    if (isControlled) {
      onValuesChange!(next);
    } else {
      setUncontrolled(next);
    }
  };

  const billTotal = useMemo(
    () =>
      bills.reduce(
        (sum, d) => sum + (parseInt(inputs[d.label] || '0', 10) || 0) * d.value,
        0
      ),
    [bills, inputs]
  );

  const coinTotal = useMemo(
    () =>
      coins.reduce(
        (sum, d) => sum + (parseInt(inputs[d.label] || '0', 10) || 0) * d.value,
        0
      ),
    [coins, inputs]
  );

  const total = billTotal + coinTotal;

  useEffect(() => {
    onTotalChange?.(total);
  }, [total, onTotalChange]);

  useEffect(() => {
    onBillTotalChange?.(billTotal);
  }, [billTotal, onBillTotalChange]);

  useEffect(() => {
    onCoinTotalChange?.(coinTotal);
  }, [coinTotal, onCoinTotalChange]);

  const handleChange = (label: string, value: string) => {
    if (/^\d*$/.test(value)) {
      setInputs({ ...inputs, [label]: value });
    }
  };

  const inputSx = {
    width: 88,
    mr: 2,
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: alpha(theme.palette.background.paper, 0.6),
      transition: 'box-shadow .2s ease',
      '&:hover': { boxShadow: `0 0 0 2px ${alpha(primary, 0.08)}` },
      '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(primary, 0.16)}` },
    },
    '& input': {
      textAlign: 'center' as const,
      fontWeight: 700,
    },
  };

  const showBills = bills.length > 0;
  const showCoins = coins.length > 0;

  return (
    <>
      {showBills && (
        <>
          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
            <BillsIcon size={20} color={primary} />
            <Typography fontWeight={700}>{billsLabel}</Typography>
          </Box>

          {bills.map((d, index) => (
            <Box key={d.label} display="flex" alignItems="center" mb={1}>
              <Typography sx={{ width: 56, color: 'text.secondary' }} aria-hidden>
                {d.label}
              </Typography>
              <TextField
                type="text"
                size="small"
                aria-label={`Count of ${d.label} ${billsLabel.toLowerCase()}`}
                value={inputs[d.label] || ''}
                onChange={(e) => handleChange(d.label, e.target.value)}
                sx={inputSx}
                inputRef={(el) => (billRefs.current[index] = el)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (index < bills.length - 1) {
                      billRefs.current[index + 1]?.focus();
                    } else if (showCoins) {
                      coinRefs.current[0]?.focus();
                    }
                  }
                }}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  enterKeyHint: 'done',
                }}
              />
              <Typography flex={1} textAlign="right" fontFamily="monospace">
                ${(parseInt(inputs[d.label] || '0', 10) * d.value).toFixed(2)}
              </Typography>
            </Box>
          ))}
        </>
      )}

      {showBills && showCoins && <Divider sx={{ my: 2 }} />}

      {showCoins && (
        <>
          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
            <CoinsIcon size={18} color={primary} />
            <Typography fontWeight={700}>{coinsLabel}</Typography>
          </Box>

          {coins.map((d, index) => (
            <Box key={d.label} display="flex" alignItems="center" mb={1}>
              <Typography sx={{ width: 56, color: 'text.secondary' }} aria-hidden>
                {d.label}
              </Typography>
              <TextField
                type="text"
                size="small"
                aria-label={`Count of ${d.label} ${coinsLabel.toLowerCase()}`}
                value={inputs[d.label] || ''}
                onChange={(e) => handleChange(d.label, e.target.value)}
                sx={inputSx}
                inputRef={(el) => (coinRefs.current[index] = el)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (index < coins.length - 1) {
                      coinRefs.current[index + 1]?.focus();
                    } else {
                      coinRefs.current[index]?.blur();
                    }
                  }
                }}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  enterKeyHint: 'done',
                }}
              />
              <Typography flex={1} textAlign="right" fontFamily="monospace">
                ${(parseInt(inputs[d.label] || '0', 10) * d.value).toFixed(2)}
              </Typography>
            </Box>
          ))}
        </>
      )}
    </>
  );
};
